import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { ShoppingCart, Menu, X, LogOut, User, Home, Package } from 'lucide-react';

function NavbarNew() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdown, setIsProfileDropdown] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch cart count when token changes or cartUpdated event fires
  useEffect(() => {
    if (token) {
      fetchCartCount();
    } else {
      setCartCount(0);
    }
  }, [token, refreshTrigger]);

  // Listen for cart update events from Cart page
  useEffect(() => {
    const handleCartUpdated = () => {
      setRefreshTrigger(t => t + 1);
    };

    window.addEventListener('cartUpdated', handleCartUpdated);
    return () => window.removeEventListener('cartUpdated', handleCartUpdated);
  }, []);

  const fetchCartCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setCartCount(data.data?.length || 0);
    } catch (error) {
      console.log('Could not fetch cart count');
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsProfileDropdown(false);
    setIsMenuOpen(false);
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/" className="flex items-center gap-2 no-underline">
              <span className="text-2xl">⚕️</span>
              <span className="font-bold text-lg md:text-xl text-slate-900">HCS Enterprises</span>
            </Link>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors flex items-center gap-2"
            >
              <Home size={20} />
              Home
            </Link>
            <Link
              to="/products"
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
            >
              Products
            </Link>
            <Link
              to="/about"
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
            >
              About Us
            </Link>
            <Link
              to="/contact"
              className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
            >
              Contact
            </Link>
          </div>

          {/* Right Side - Orders, Cart & Auth */}
          <div className="flex items-center gap-4 md:gap-6">
            {/* Orders Icon - only if logged in */}
            {token && (
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/my-orders"
                  className="relative text-slate-700 hover:text-teal-600 transition-colors"
                >
                  <Package size={24} />
                </Link>
              </motion.div>
            )}

            {/* Cart Icon */}
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/cart"
                className="relative text-slate-700 hover:text-teal-600 transition-colors"
              >
                <ShoppingCart size={24} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>
            </motion.div>

            {/* Auth Section */}
            {token && user ? (
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => setIsProfileDropdown(!isProfileDropdown)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
                >
                  <User size={18} className="text-teal-600" />
                  <span className="hidden sm:inline text-sm font-semibold text-slate-900 truncate max-w-xs">
                    {user.name || 'User'}
                  </span>
                </motion.button>

                {/* Profile Dropdown */}
                <AnimatePresence>
                  {isProfileDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden"
                    >
                      <Link
                        to="/profile"
                        className="block px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors font-medium"
                        onClick={() => setIsProfileDropdown(false)}
                      >
                        👤 My Profile
                      </Link>
                      <Link
                        to="/my-orders"
                        className="block px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors font-medium"
                        onClick={() => setIsProfileDropdown(false)}
                      >
                        📋 My Orders
                      </Link>
                      <Link
                        to="/cart"
                        className="block px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors font-medium"
                        onClick={() => setIsProfileDropdown(false)}
                      >
                        🛒 My Cart
                      </Link>
                      {user.role === 'admin' && (
                        <Link
                          to="/secret-panel"
                          className="block px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors font-medium"
                          onClick={() => setIsProfileDropdown(false)}
                        >
                          ⚙️ Admin Panel
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition-colors font-medium flex items-center gap-2 border-t border-slate-200"
                      >
                        <LogOut size={18} />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-3">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/user-login"
                    className="px-4 py-2 text-slate-700 hover:text-teal-600 font-semibold transition-colors"
                  >
                    Login
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/user-register"
                    className="px-6 py-2 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors"
                  >
                    Sign Up
                  </Link>
                </motion.div>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-slate-700 hover:text-teal-600"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-slate-200"
            >
              <div className="px-4 py-4 space-y-3">
                <Link
                  to="/"
                  className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/products"
                  className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Products
                </Link>
                <Link
                  to="/about"
                  className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About Us
                </Link>
                <Link
                  to="/contact"
                  className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </Link>

                <div className="border-t border-slate-200 pt-3">
                  {token && user ? (
                    <>
                      <Link
                        to="/profile"
                        className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        👤 My Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 font-medium flex items-center gap-2"
                      >
                        <LogOut size={18} />
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/user-login"
                        className="block px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Login
                      </Link>
                      <Link
                        to="/user-register"
                        className="block px-4 py-3 rounded-lg bg-teal-500 text-white font-medium text-center mt-2"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}

export default NavbarNew;
